"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { Search, Lock, Plug, Workflow } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import SectionCTA from "@/components/ui/SectionCTA";
import SectionEyebrow from "@/components/ui/SectionEyebrow";
import Footer from "@/components/layout/Footer";
import FAQAccordion from "@/components/ui/FAQAccordion";
import { consultingFaqs as faqs } from "@/lib/faqs";

// This preview is CSS-hidden below `lg`, but a plain import still ships and
// hydrates its JS on mobile for markup nobody sees. Code-split it and only
// mount it once we know we're actually at a `lg` viewport.
const ChatAppPreview = dynamic(() => import("@/components/launchlemonade/ChatAppPreview"));

const services = [
  {
    icon: Search,
    title: "AI Strategy",
    what: "We audit your workflows, identify the highest-ROI automation targets, and give you a prioritised roadmap.",
    so_what: "You stop guessing where AI fits and start with the thing that saves the most hours.",
    deliverable: "A scoped roadmap with timelines, costs, and expected ROI for each opportunity.",
  },
  {
    icon: Workflow,
    title: "Custom AI Agents",
    what: "We build AI agents tailored to your firm's workflows. Due diligence, client onboarding, report generation, compliance checks, email triage.",
    so_what: "The repetitive work that eats your senior team's time gets handled automatically, with human review where it matters.",
    deliverable: "Working agents deployed into your existing tools, with your team trained to use them.",
  },
  {
    icon: Lock,
    title: "Private AI Infrastructure",
    what: "We deploy AI models on your own servers or private cloud. Your data never leaves your perimeter.",
    so_what: "Your firm gets the power of AI without the compliance risk. Client data stays where regulators expect it.",
    deliverable: "A private AI environment, fully configured, with the models tuned for your document types.",
  },
  {
    icon: Plug,
    title: "System Integration",
    what: "We connect AI to the tools your firm already uses. CRM, document management, practice management, email, calendar.",
    so_what: "No new tools to learn. AI works inside the systems your team already lives in.",
    deliverable: "AI capabilities embedded into your existing stack, not bolted on top.",
  },
];

const process = [
  {
    step: "1",
    title: "Audit",
    duration: "Week 1-2",
    desc: "We map your workflows, identify where AI creates the most leverage, and define exactly what we're building. You get a clear scope, timeline, and investment estimate before anything is built.",
  },
  {
    step: "2",
    title: "Build",
    duration: "Week 3-6",
    desc: "We build in weekly sprints with demos every Friday. You see working software every week. Your team gives feedback, we adjust. No big reveal at the end.",
  },
  {
    step: "3",
    title: "Deploy and train",
    duration: "Week 7-8",
    desc: "We deploy to your infrastructure, integrate with your existing tools, and train your team to use and maintain everything. You're not dependent on us to keep things running.",
  },
];

export default function ConsultingPage() {
  const shouldReduceMotion = useReducedMotion();
  const anim = shouldReduceMotion ? {} : { opacity: 0, y: 16 };
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return (
    <main id="main-content" className="section-black-bg">
      <section className="relative min-h-[85vh] flex items-center pt-32 pb-20 overflow-hidden">
        <div className="w-full mx-auto max-w-[1440px] px-6 md:px-12 relative z-10 flex flex-col lg:flex-row items-center gap-16">
          <div className="w-full lg:w-[52%] max-w-2xl">
            <motion.p className="text-[11px] font-heading font-medium tracking-[0.15em] text-eyebrow mb-5 uppercase" initial={anim} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              AI Consulting
            </motion.p>
            <motion.h1 className="fluid-h1 font-heading heading-pure mb-6" style={{ fontWeight: 450 }} initial={anim} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05 }}>
              We build the AI{" "}
              <span>your firm actually needs.</span>
            </motion.h1>
            <motion.p className="fluid-body-lg text-muted-foreground prose-narrow mb-5" initial={anim} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
              Not a proof of concept. Not a slide deck. Working AI agents deployed into your workflows, saving your team hours every week. We figure out where AI fits in your firm, then we build it.
            </motion.p>

            <motion.div
              className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-10"
              initial={anim}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.14 }}
            >
              {["Strategy", "Custom agents", "Private infrastructure", "System integration"].map((item, i) => (
                <span key={i} className="flex items-center gap-1.5 text-[12px] text-foreground/60">
                  <span className="w-1 h-1 rounded-full bg-primary/60 shrink-0" />
                  {item}
                </span>
              ))}
            </motion.div>

            <motion.div className="flex flex-col sm:flex-row gap-3" initial={anim} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.18 }}>
              <Link href="/book?type=consulting" className="btn-gold btn-gold-glow">Book a Consultation</Link>
            </motion.div>
          </div>

          {isDesktop && (
            <motion.div
              className="hidden w-full lg:block lg:w-[48%]"
              initial={shouldReduceMotion ? {} : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <ChatAppPreview showFloatingPanels={false} />
            </motion.div>
          )}
        </div>
      </section>

      <section className="fluid-section" aria-labelledby="services-heading">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <motion.div
            className="mb-12 md:mb-14"
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <SectionEyebrow label="Capabilities" align="left" className="mb-4" />
            <h2 id="services-heading" className="fluid-h2 font-heading font-bold mb-2">Four things we do</h2>
            <p className="text-[13px] text-muted-foreground prose-narrow">Each one starts with your workflows and ends with working software.</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {services.map((s, i) => {
              const isFeatured = i === 0;
              return (
                <motion.div
                  key={i}
                  className={`stat-glass p-7 md:p-8 flex flex-col ${
                    isFeatured
                      ? "lg:col-span-3 lg:flex-row lg:items-stretch lg:gap-10"
                      : ""
                  }`}
                  initial={shouldReduceMotion ? {} : { opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.45 }}
                >
                  <div className={isFeatured ? "lg:w-[30%] lg:shrink-0 mb-6 lg:mb-0" : "mb-5"}>
                    {isFeatured && (
                      <span
                        className="inline-flex items-center gap-1.5 mb-4 rounded-full px-2.5 py-1 font-accent text-[10px] font-semibold uppercase tracking-[0.14em]"
                        style={{ color: "var(--icon-gold-text)", background: "var(--icon-gold-bg)" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--icon-gold-text)" }} />
                        Start here
                      </span>
                    )}
                    <div className="flex items-center gap-3.5">
                      <span
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: "var(--icon-gold-bg)", color: "var(--icon-gold-text)" }}
                      >
                        <s.icon size={20} strokeWidth={1.75} aria-hidden="true" />
                      </span>
                      <h3 className={`font-heading font-semibold ${isFeatured ? "text-lg md:text-xl" : "text-base"}`}>
                        {s.title}
                      </h3>
                    </div>
                  </div>

                  <div className={isFeatured ? "lg:flex-1 flex flex-col" : "flex flex-col flex-1"}>
                    <p className="text-[13px] text-muted-foreground leading-relaxed mb-2.5">{s.what}</p>
                    <p className="text-[13px] text-foreground/70 leading-relaxed mb-5">{s.so_what}</p>
                    <div
                      className="mt-auto rounded-lg border px-3.5 py-3"
                      style={{
                        borderColor: "color-mix(in srgb, var(--primary) 22%, transparent)",
                        background: "color-mix(in srgb, var(--primary) 6%, transparent)",
                      }}
                    >
                      <p className="font-accent text-[10px] font-semibold uppercase tracking-[0.14em] text-primary/70 mb-1">
                        You get
                      </p>
                      <p className="text-[12.5px] text-foreground/80 leading-relaxed">{s.deliverable}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-alt fluid-section" aria-labelledby="process-heading">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <motion.div
            className="mb-16"
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h2 id="process-heading" className="fluid-h2 font-heading font-bold mb-2">Audit. Build. Deploy</h2>
            <p className="text-[13px] text-muted-foreground prose-narrow">Every engagement follows the same shape. No ambiguity about what happens when.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 max-w-3xl">
            {process.map((p, i) => (
              <motion.div
                key={i}
                className={`p-6 md:p-8 ${i < process.length - 1 ? "md:border-r border-b md:border-b-0" : ""}`}
                style={i < process.length - 1 ? { borderColor: "hsl(222 16% 16% / 0.15)" } : undefined}
                initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-accent text-[20px] font-bold text-primary/40">{p.step}</span>
                  <span className="text-[10px] text-muted-foreground font-accent uppercase tracking-wider">{p.duration}</span>
                </div>
                <h3 className="font-heading font-semibold text-base mb-2">{p.title}</h3>
                <p className="text-[13px] text-muted-foreground leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="fluid-section" aria-labelledby="who-heading">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <motion.div
            className="max-w-2xl"
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h2 id="who-heading" className="fluid-h2 font-heading font-bold mb-4">Built for firms that run on client work</h2>
            <p className="text-[14px] text-muted-foreground leading-relaxed mb-6">
              Accounting, advisory, consulting, and legal firms. You don&apos;t need a technical team. You need someone who understands how professional services firms work and can build AI that fits.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Accounting firms", "Advisory practices", "Consulting firms", "Legal teams", "Fractional CFOs"].map((label) => (
                <span
                  key={label}
                  className="text-[11px] font-accent font-medium text-muted-foreground px-3 py-1.5 rounded-md border"
                  style={{ borderColor: "hsl(222 16% 16% / 0.2)" }}
                >
                  {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-alt py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <motion.div
            className="glass-card p-6 md:p-8 max-w-2xl"
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <h3 className="font-heading font-semibold text-base mb-2">Not sure where to start?</h3>
            <p className="text-[13px] text-muted-foreground leading-relaxed mb-4">
              Start with the strategy audit. We&apos;ll tell you exactly what&apos;s worth building and what isn&apos;t. No commitment to build beyond that. If AI doesn&apos;t make sense for your firm, we&apos;ll say so.
            </p>
            <Link href="/book?type=consulting" className="text-[12px] font-heading font-semibold text-primary/70 hover:text-primary transition-colors">
              Book a free scoping call &rarr;
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="fluid-section" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-[900px] px-6 md:px-12 text-center">
          <motion.h2
            id="faq-heading"
            className="font-heading font-bold mb-10"
            style={{
              fontSize: "clamp(2rem, 1rem + 3.5vw, 3.5rem)",
              lineHeight: 1,
              letterSpacing: "-0.035em",
            }}
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            Common questions
          </motion.h2>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <SectionCTA title="Ready to build?" buttonText="Book a Consultation" href="/book?type=consulting" />
      </div>
      <Footer />
    </main>
  );
}
